import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  ogUrl?: string;
}

/**
 * Sets a meta tag and returns the undo for it: restore the previous content,
 * or remove the tag entirely if this call is what created it.
 *
 * Without the undo, a page that sets `og:image` (the guide identity pages do)
 * leaves its image attached to every page visited afterwards.
 */
function upsertMeta(property: string, content: string): () => void {
  const selector = `meta[property="${property}"]`;
  const existing = document.querySelector(selector) as HTMLMetaElement | null;

  if (existing) {
    const previous = existing.getAttribute('content');
    existing.setAttribute('content', content);
    return () => {
      if (previous === null) existing.removeAttribute('content');
      else existing.setAttribute('content', previous);
    };
  }

  const created = document.createElement('meta');
  created.setAttribute('property', property);
  created.setAttribute('content', content);
  document.head.appendChild(created);
  return () => created.remove();
}

/** Sets document title, meta description, and basic Open Graph tags for public pages. */
export function SeoHead({ title, description, ogImage, ogUrl }: SeoHeadProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);

    const undoOg = [
      upsertMeta('og:title', title),
      upsertMeta('og:description', description),
      upsertMeta('og:type', 'website'),
      ...(ogUrl ? [upsertMeta('og:url', ogUrl)] : []),
      ...(ogImage ? [upsertMeta('og:image', ogImage)] : []),
    ];

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription);
      }
      // Reverse order, so a tag created here is removed before anything that
      // depended on it being present.
      for (const undo of undoOg.reverse()) undo();
    };
  }, [title, description, ogImage, ogUrl]);

  return null;
}
